import { useMemo } from 'react'
import useSWR from 'swr'

import { apiFetcher } from '@/data/fetcher'
import { Item } from '@/interfaces/cash-register/Item'
import { StockItem } from '@/interfaces/warehouse/StockItem'
import {
	availableToAdd,
	buildStockBySkuId,
	maxQuantityForLine,
} from '@/lib/warehouse/stockAvailability'

export function useCashRegisterStock(
	enabled: boolean,
	items: Item[],
	warehouseId?: string | null,
) {
	const { data: stockItemsResponse } = useSWR<{ data?: StockItem[] }>(
		enabled && warehouseId
			? `/api/v2/stock-items?warehouseId=${warehouseId}`
			: null,
		apiFetcher,
	)

	const stockBySkuId = useMemo(() => {
		if (!warehouseId || !stockItemsResponse?.data) {
			return null
		}

		// SKU s neomezenou zásobou (isUnlimited) se nesleduje, jinak by quantityAvailable 0 znamenalo "vyprodáno"
		const unlimited = new Set(
			stockItemsResponse.data.filter((item) => item.isUnlimited).map((item) => item.skuId),
		)

		return buildStockBySkuId(
			stockItemsResponse.data.filter((item) => !unlimited.has(item.skuId)),
		)
	}, [warehouseId, stockItemsResponse?.data])

	const getAvailableToAdd = useMemo(
		() => (skuId: string) => availableToAdd(skuId, items, stockBySkuId),
		[items, stockBySkuId],
	)

	const getMaxQuantityForLine = useMemo(
		() => (skuId: string, lineIndex: number) =>
			maxQuantityForLine(skuId, items, lineIndex, stockBySkuId),
		[items, stockBySkuId],
	)

	return {
		warehouseId: warehouseId ?? null,
		stockBySkuId,
		getAvailableToAdd,
		getMaxQuantityForLine,
	}
}
